// SSE framing — the pure half every live view in this app shares.
//
// Every live view dials with `fetch` + a stream reader rather than `EventSource`
// (the dev `dev_pat_` bearer has to ride along, and native EventSource can only
// send cookies), so the wire format is ours to parse. It lives here, apart from
// the loop that drives it (`sse-subscribe.ts`), because it is pure and testable
// against a string where the loop needs a socket.

import type { z } from 'zod';

export type StreamStatus = 'connecting' | 'live' | 'done' | 'error';

/**
 * One SSE frame. `event` + the joined `data:` lines, plus the two connection
 * fields the API publishes (`apps/api/src/middleware/sse-stream.ts`):
 *
 *   - `id` — the RESUME TOKEN, echoed back as `Last-Event-ID` on the next
 *     connection so a reconnect tails from where this one stopped instead of
 *     re-backfilling the whole log. Absent on the `done`/`bye` frames on
 *     purpose: the spec's last-event-ID buffer is NOT reset between frames, so
 *     an omitted field leaves the reader holding the previous row's token —
 *     which IS the resume point. Consumers must therefore only overwrite their
 *     held token when this is present.
 *   - `retry` — the reconnect backoff, published ONCE on a connection's first
 *     frame. Absent on every later frame; the consumer keeps the last value.
 */
export type SseFrame = { event: string; data: string; id?: string; retry?: number };

/**
 * Parse one `\n\n`-delimited SSE block. Returns null for a block carrying
 * nothing any consumer reads — a bare comment, a keepalive, an `event:` with no
 * `data:`. Kept pure + exported for a unit check.
 *
 * A PACING-ONLY block is the one data-less block that survives, as `data: ''`.
 * `apps/api/src/middleware/sse-stream.ts` writes a connection's FIRST heartbeat
 * as `: heartbeat\nretry: 3000\n\n` — a comment plus the field — precisely so a
 * stream that produces no rows for ten minutes still publishes its reconnect
 * pacing. This parser used to drop that block whole, on the premise that "our
 * only producer never sends one"; the heartbeat falsified it, and the cost was
 * silent: `retry:` is the operator's one lever over how hard a fleet of open
 * tabs re-dials while the api rolls, and it stopped reaching the quiet streams
 * that most need it. `sse-subscribe.ts` consumes the field and never forwards a
 * `data: ''` frame to a view, so no consumer sees an empty payload.
 *
 * A bare `id:` still yields nothing: no route emits one, and a resume token with
 * no row behind it is not a resume point.
 */
export function parseSseFrame(block: string): SseFrame | null {
  let event = 'message';
  let id: string | undefined;
  let retry: number | undefined;
  const data: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
    else if (line.startsWith('id:')) id = line.slice(3).trim();
    else if (line.startsWith('retry:')) {
      // Per the spec a non-numeric `retry:` is IGNORED, not an error: the field
      // paces reconnects, and a proxy mangling it must not stop the stream.
      const ms = Number(line.slice(6).trim());
      if (Number.isFinite(ms) && ms >= 0) retry = ms;
    }
  }
  // Neither data nor pacing: a keepalive with nothing to say.
  if (data.length === 0 && retry === undefined) return null;
  return {
    event,
    data: data.join('\n'),
    ...(id !== undefined ? { id } : {}),
    ...(retry !== undefined ? { retry } : {}),
  };
}

/**
 * Validate one frame's JSON `data` against `schema` at the network I/O boundary.
 * SSE frames are untrusted input, so a malformed frame — invalid JSON or a shape
 * the schema rejects — is dropped and logged, never thrown: one bad frame must
 * not tear the whole stream down. Returns `null` on failure. Shared by the three
 * stream consumers so validation stays uniform.
 */
export function parseFrameData<S extends z.ZodType>(schema: S, data: string): z.infer<S> | null {
  let json: unknown;
  try {
    json = JSON.parse(data);
  } catch {
    console.warn('[sse] dropped malformed frame: invalid JSON');
    return null;
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    console.warn('[sse] dropped malformed frame: schema mismatch', parsed.error.issues);
    return null;
  }
  return parsed.data;
}
